/* eslint-disable @typescript-eslint/no-unused-vars */
import { Box, Flex, Text, useMediaQuery } from '@chakra-ui/react';
import { Chart as ChartJS, registerables, type ChartTypeRegistry } from 'chart.js';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, memo, useCallback } from 'react';
import { Chart as ReactChart } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';

import { NewDimItemData, NewTotalValues } from 'models/response/blockOutput.interface';
import { IndicatorDriver } from 'models/response/driver.interface';
import { CHART_COLORS } from 'projectConstants';
import {
  ShowSidePanel,
  getBlockDriversData,
  getChartFilter,
  getChartType,
  getPlanPageChartinnerWidth,
  getSelectedDriver,
  getValuesForSlider,
  inputsPageShow,
  planPageShow,
  setChartType,
  setPlanPageInnerWidth,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import getChartOptionsNew from 'utils/getChartOptionsNew';
import { AppDispatch } from 'utils/GlobalHelpers';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import { usePlanPageRevampVariant } from '../PlanPageUIRevamp/usePlanPageUiRevamp';
import {
  PLAN_CHART_SERIES_COLORS,
  PLAN_CHART_VIEW_HEIGHT,
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_TEXT,
} from '../PlanPageUIRevamp/planPageRevampUi';

import ChartTypeSwitcher from './ChartTypeSwitcher';
import CustomRangeSlider from './CustomRangeSlider';

import type { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';

ChartJS.register(...registerables);

interface Properties {
  chartData: NewDimItemData[];
  chartLabels: string[];
  tabData: any;
  activeTabIndex: any;
  hideToolbar?: boolean;
  isLegend?: boolean;
  variant?: 'legacy' | 'revamp';
  exportRef?: React.MutableRefObject<ChartJSOrUndefined<keyof ChartTypeRegistry> | null>;
  plotHeight?: string;
  fillPlotHeight?: boolean;
}

const Chart = ({
  chartData,
  chartLabels,
  hideToolbar = false,
  isLegend: isLegendProperty,
  variant = 'legacy',
  exportRef,
  plotHeight,
  fillPlotHeight = false,
}: Properties) => {
  const isRevampUi = usePlanPageRevampVariant(variant);
  const [isMobile] = useMediaQuery('(min-width: 375px)' && '(max-width: 649px)');
  const [chartKey, setChartKey] = useState(0);
  const [isLegendInternal, setIsLegendInternal] = useState(true);
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());
  const isLegend = isLegendProperty ?? isLegendInternal;
  const setIsLegend = setIsLegendInternal;
  const dispatch: AppDispatch = useDispatch();
  const showInputPage = useSelector(inputsPageShow);
  const showPlanPage = useSelector(planPageShow);
  const currentDriver = useSelector(getSelectedDriver);
  const sliderLables = useSelector(getValuesForSlider);

  const containerReference = useRef<HTMLDivElement>(null);
  const innerWidth = useSelector(getPlanPageChartinnerWidth);
  const baseScenario = useSelector(getBaseScenario);
  const chartFilter = useSelector(getChartFilter);
  const showSidePanel = useSelector(ShowSidePanel);
  const blockDrivers = useSelector(getBlockDriversData);
  const themeColors = useSelector(getPlanPageThemeColors);
  const isRevamp = isRevampUi;
  const filteredDrivers = blockDrivers?.filter(
    (driver: IndicatorDriver) => driver.type === 'indicator',
  );
  // Convert to Date object
  const startDateObject = new Date(baseScenario?.start_date);
  startDateObject.setMonth(startDateObject.getMonth() - 1);

  // Format 1: Year 2023
  const yearFormat = `FY${startDateObject.getFullYear().toString().slice(-2)}`;

  // Format 2: Quarter-wise like Q4-23
  const quarterFormat = `Q${Math.ceil((startDateObject.getMonth() + 1) / 3)}-${String(
    yearFormat,
  ).slice(-2)}`;

  // Format 3: Nov-23
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const monthFormat = `${monthNames[startDateObject.getMonth()]}-${String(yearFormat).slice(-2)}`;

  const targetDate = () => {
    if (chartFilter === 'M') {
      return monthFormat;
    }
    if (chartFilter === 'Q') {
      return quarterFormat;
    }
    return yearFormat;
  };

  const chartSeriesSignature = useMemo(
    () => chartData.map((entry) => entry.name).join('\u001F'),
    [chartData],
  );

  useEffect(() => {
    setChartKey((previousKey) => previousKey + 1);
  }, [chartFilter, baseScenario, chartSeriesSignature]);

  useEffect(() => {
    setHiddenDatasets(new Set());
  }, [chartSeriesSignature]);

  const toggleDatasetVisibility = useCallback((index: number) => {
    setHiddenDatasets((previous) => {
      const next = new Set(previous);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const chartTypeState = useSelector(getChartType);

  const toggleChartType = (newType: string) => dispatch(setChartType(newType));

  const legendMargin = {
    id: 'legendMargin',
    beforeInit(chart: any) {
      const originalFit = chart.legend.fit;

      chart.legend.fit = function fit() {
        originalFit.bind(chart.legend)();
        this.height += 25;
      };
    },
  };

  const datasets = useMemo(
    () =>
      chartData.map((entry: NewDimItemData, index: number) => {
        const data = entry?.value?.map((value: NewTotalValues) => value.value) || [];
        if (data.some((value) => value !== 0)) {
          while (data.length > 0 && data[data.length - 1] === 0) {
            data.pop();
          }
        }
        const seriesColor = isRevamp
          ? PLAN_CHART_SERIES_COLORS[index % PLAN_CHART_SERIES_COLORS.length]
          : CHART_COLORS[index];
        return {
          label: entry.name,
          data,
          backgroundColor: seriesColor,
          borderColor: seriesColor,
          hidden: hiddenDatasets.has(index),
        };
      }),
    [chartData, isRevamp, hiddenDatasets],
  );

  const dataOfChart = useMemo(
    () => ({
      labels: chartLabels,
      datasets,
    }),
    [chartLabels, datasets],
  );

  useLayoutEffect(() => {
    const updateContainerWidth = () => {
      setTimeout(() => {
        if (containerReference.current) {
          const containerWidth = containerReference.current.offsetWidth;
          const containerPadding =
            Number.parseInt(window.getComputedStyle(containerReference.current).paddingLeft, 10) +
            Number.parseInt(window.getComputedStyle(containerReference.current).paddingRight, 10);
          const widthWithoutPadding = containerWidth - containerPadding;
          if (widthWithoutPadding) dispatch(setPlanPageInnerWidth(widthWithoutPadding));
        }
      }, 200);
    };
    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => {
      window.removeEventListener('resize', updateContainerWidth);
    };
  }, [containerReference?.current, dataOfChart, currentDriver, showInputPage, showPlanPage]);

  const drawBackgroundPlugin = {
    id: 'drawBackground',
    beforeDraw: ({ ctx, chartArea, data, scales }: any) => {
      ctx.fillStyle = 'transparent';

      const targetLabel = targetDate();
      const targetIndex = data.labels.indexOf(`${targetLabel}`);
      if (targetIndex !== -1) {
        // Get the x-axis pixel position for the specified dataset and index
        const xPosition = scales.x.getPixelForValue(data.labels[targetIndex]);

        ctx.fillStyle = '#EEF2F6';
        ctx.fillRect(
          chartArea.left,
          chartArea.top,
          xPosition - chartArea.left,
          chartArea.bottom - chartArea.top,
        );
      } else {
        ctx.fillRect(
          chartArea.left,
          chartArea.top,
          chartArea.right - chartArea.left,
          chartArea.bottom - chartArea.top,
        );
      }
    },
  };

  return hideToolbar ? (
    <Box
      display='flex'
      flexDirection='column'
      h={isRevamp && fillPlotHeight ? '100%' : 'auto'}
      minH={isRevamp && fillPlotHeight ? 0 : undefined}
      w='100%'
    >
      <Box
        flex={isRevamp && fillPlotHeight ? '1 1 0' : undefined}
        flexShrink={0}
        minH={isRevamp ? (fillPlotHeight ? 0 : plotHeight ?? PLAN_CHART_VIEW_HEIGHT) : '180px'}
        h={
          isRevamp ? (fillPlotHeight ? undefined : plotHeight ?? PLAN_CHART_VIEW_HEIGHT) : undefined
        }
        position='relative'
        w='100%'
        px={isRevamp ? 0 : '1%'}
        py={isRevamp ? 0 : '1%'}
        mb={isRevamp ? 0 : '1%'}
        sx={isRevamp ? undefined : { height: '200px' }}
      >
        <ReactChart
          key={chartKey}
          ref={(instance) => {
            if (exportRef) {
              exportRef.current = instance;
            }
          }}
          type={chartTypeState === 'line' ? 'line' : 'bar'}
          data={dataOfChart}
          options={getChartOptionsNew({
            chartType: chartTypeState === 'line' ? 'line' : 'stacked bar',
            isLegend: false,
            aspectRatio: isRevamp ? 2.5 : 1,
            theme: isRevamp ? 'revamp' : 'legacy',
          })}
          plugins={[legendMargin, drawBackgroundPlugin]}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>

      {dataOfChart?.labels && !isRevamp && (
        <Box flexShrink={0} px={isRevamp ? 0 : undefined} pb={2}>
          <CustomRangeSlider labels={sliderLables} variant={variant} />
        </Box>
      )}

      {isRevamp && datasets.length > 0 && (
        <Box
          flexShrink={0}
          borderTop='1px solid'
          borderColor={themeColors.border}
          bg={themeColors.surface}
          px={4}
          py={3}
          mx={`-${PLAN_HEADER_HORIZONTAL_PADDING}`}
        >
          <Flex
            ref={containerReference}
            flexWrap='wrap'
            gap={4}
            mx={`${PLAN_HEADER_HORIZONTAL_PADDING}`}
          >
            {datasets.map((_data: any, index: number) => {
              const isHidden = hiddenDatasets.has(index);

              return (
                <Flex
                  alignItems='center'
                  gap='5px'
                  key={index}
                  cursor='pointer'
                  opacity={isHidden ? 0.45 : 1}
                  onClick={() => toggleDatasetVisibility(index)}
                >
                  <Box
                    bg={_data.backgroundColor}
                    h='12px'
                    w='12px'
                    borderRadius='full'
                    flexShrink={0}
                  />
                  <Text
                    {...PLAN_TEXT.labelSm}
                    color={themeColors.textDark}
                    textDecoration={isHidden ? 'line-through' : undefined}
                  >
                    {_data?.label}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      )}

      {!isRevamp && isLegend && (
        <Flex
          ref={containerReference}
          display='flex'
          justifyContent='flex-start'
          alignItems='self-start'
          flexDirection='row'
          flexShrink={0}
          flexWrap='wrap'
        >
          {datasets.map((_data: any, index: number) => (
            <Flex alignItems='center' p='2px' ml={1} key={index}>
              <Box bg={CHART_COLORS[index]} h='13px' w='13px' mr='0.1rem' />
              <Text color='black' fontSize='13px' fontFamily='regular_roboto' fontWeight='500'>
                {_data?.label}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
    </Box>
  ) : (
    <>
      <Flex
        position='relative'
        marginBottom='1%'
        padding='1%'
        height={isMobile ? (innerWidth ? `${innerWidth / 1.5}px` : '35vh') : '35vh'}
        flexShrink={0}
      >
        <ReactChart
          key={chartKey}
          type={chartTypeState === 'line' ? 'line' : 'bar'}
          data={dataOfChart}
          options={getChartOptionsNew({
            chartType: chartTypeState === 'line' ? 'line' : 'stacked bar',
            isLegend: false,
            aspectRatio: 1,
          })}
          plugins={[legendMargin, drawBackgroundPlugin]}
        />
      </Flex>
      <Flex
        ref={containerReference}
        display={'flex'}
        justifyContent={isMobile ? 'center' : 'space-between'}
        alignItems='self-start'
        flexDirection='row'
        pt={isMobile ? '0.7rem' : 0}
      >
        <Flex maxW={isMobile ? '100&' : '70%'} width={isMobile ? '100&' : '70%'} flexWrap={'wrap'}>
          {isLegend &&
            datasets.map((_data: any, index: number) => (
              <Flex alignItems={'center'} p={'2px'} ml={1} key={index}>
                <Box
                  display={'flex'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  bg={CHART_COLORS[index]}
                  h='13px'
                  w='13px'
                  mr='0.1rem'
                ></Box>
                <Box display={'flex'} justifyContent={'center'} alignItems={'center'} h='13px'>
                  <Text
                    color='black'
                    fontSize={'13px'}
                    fontFamily={'regular_roboto'}
                    fontWeight={'500'}
                  >
                    {_data?.label}
                  </Text>
                </Box>
              </Flex>
            ))}
        </Flex>
        {!isMobile && (
          <Flex>
            <ChartTypeSwitcher
              viewMode={chartTypeState}
              onChange={toggleChartType}
              isLegend={isLegend}
              setIsLegend={setIsLegend}
            />
          </Flex>
        )}
      </Flex>
      {dataOfChart?.labels && !isRevamp && (
        <Box>
          <CustomRangeSlider labels={sliderLables} />
        </Box>
      )}
      {isMobile && (
        <Flex
          ps='0.6rem'
          py='0.8rem'
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <ChartTypeSwitcher
            viewMode={chartTypeState}
            onChange={toggleChartType}
            isLegend={isLegend}
            setIsLegend={setIsLegend}
          />
        </Flex>
      )}
    </>
  );
};

export default memo(Chart);
